import { DatePicker, Input, InputNumber, Select, Switch } from 'antd';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { ImageInput } from '../../components/image-input';
import { LabeledField } from '../../components/form-fields/labeled-field';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { Section } from '../../components/form-fields/section';
import { TimezoneSelect } from '../../components/timezone-select';
import { BusinessUnitsSelectTree } from '../../components/region-selectors/business-units-select-tree';
import { RichText } from '../../components/rich-text';
import {
    useCategoryStore,
    useOfferStore,
    useRppPrizeCouponStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { localizeLanguage, localizeLanguageShort } from '../../utils/languages';
import { useNavigation } from '../../utils/use-navigation';
import {
    buildPayload,
    cleanProvinceIds,
    couponToForm,
    createInitialForm,
    RppPrizeCouponForm,
    YupRppPrizeCouponForm,
} from '../../utils/rpp-prize-coupon';
import style from './style.module.scss';

const DATE_TIME_FORMAT = 'YYYY-MM-DD hh:mm A';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (value) {
            const action = this.resolve(yup.ref('action'));
            return !(value === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

interface Inventory {
    granted: number;
    remaining: number | null;
}

export const RppPrizeCouponPage = observer(() => {
    const params = useParams<Params>();
    const store = useRppPrizeCouponStore();
    const categoryStore = useCategoryStore();
    const offerStore = useOfferStore();
    const navigation = useNavigation();

    const [inventory, setInventory] = useState<Inventory | null>(null);

    const formik = useFormik<RppPrizeCouponForm>({
        initialValues: createInitialForm(),
        validateOnMount: true,
        validationSchema: YupRppPrizeCouponForm,
        onSubmit: () => void 0,
    });

    if (!YupParams.isValidSync(params)) {
        navigation.goToRppPrizeCoupons();
    }

    const isEdit = params.action === 'edit' && !!params.id;

    useEffect(() => {
        categoryStore.fetchActiveCategories();
        offerStore.fetchOffers();
    }, [categoryStore, offerStore]);

    const persist = useCallback(async (): Promise<boolean> => {
        const payload = buildPayload(formik.values);
        const result =
            params.action === 'edit' && params.id
                ? await store.updateCoupon(Number(params.id), payload)
                : await store.createCoupon(payload);
        if (result) {
            setInventory({
                granted: result.grantedQuantity,
                remaining: result.remainingQuantity,
            });
        }
        return result !== null;
    }, [formik.values, params.action, params.id, store]);

    // Used by the unsaved-changes modal: throw on failure so the user stays on
    // the page and can retry the save.
    const onModalSave = useCallback(async () => {
        const ok = await persist();
        if (!ok) {
            throw new Error('Failed to save RPP prize coupon');
        }
    }, [persist]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: onModalSave,
    });

    const onSave = useCallback(async () => {
        const ok = await persist();
        if (ok) {
            clearDirty(() => navigation.goToRppPrizeCoupons());
        }
    }, [persist, clearDirty, navigation]);

    const onDeleteConfirm = useCallback(async () => {
        if (!params.id) {
            NotificationManager.showError(
                "Can't delete this RPP prize coupon."
            );
            return;
        }
        const response = await store.deleteCoupon(
            Number(params.id),
            formik.values.type
        );
        if (!response.err) {
            clearDirty(() => navigation.goToRppPrizeCoupons());
        }
    }, [params.id, store, clearDirty, navigation, formik.values.type]);

    useEffect(() => {
        if (params.action === 'edit' && params.id) {
            store
                .fetchCoupon(params.id)
                .then((coupon) => {
                    if (!coupon) {
                        throw new Error('RPP prize coupon not found');
                    }
                    formik.resetForm({ values: couponToForm(coupon) });
                    setInventory({
                        granted: coupon.grantedQuantity,
                        remaining: coupon.remainingQuantity,
                    });
                })
                .catch(() => navigation.goToRppPrizeCoupons());
        }
    }, []);

    const { values, setFieldValue, handleChange, handleBlur } = formik;

    const renderTopBarActions = () => {
        const saveDisabled = !formik.isValid;
        if (isEdit) {
            return (
                <>
                    <DeleteButton
                        onClick={onDeleteConfirm}
                        loading={store.isDeleting}
                    />
                    <SaveButton
                        onClick={onSave}
                        loading={store.isSaving}
                        disabled={saveDisabled}
                    />
                </>
            );
        }
        return (
            <SaveButton
                onClick={onSave}
                loading={store.isSaving}
                disabled={saveDisabled}
            />
        );
    };

    const title = isEdit
        ? `RPP Prize Coupon #${params.id}`
        : 'New RPP Prize Coupon';

    return (
        <>
            <PageTopBar
                title={title}
                goBack={{
                    callback: () => navigation.goToRppPrizeCoupons(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <div className="content_with_margins">
                <Section title="Schedule">
                    <div className={style.section_fields}>
                        <LabeledField
                            label="Type"
                            helperText={
                                isEdit
                                    ? 'The promo type cannot be changed after creation.'
                                    : 'Coupons are prize/inventory promos; in-store promos are public display promos.'
                            }
                        >
                            <Select
                                value={values.type}
                                disabled={isEdit}
                                onChange={(type) => setFieldValue('type', type)}
                                style={{ width: 320 }}
                            >
                                <Select.Option value="coupon">
                                    Coupon
                                </Select.Option>
                                <Select.Option value="in_store">
                                    In-store promo
                                </Select.Option>
                            </Select>
                        </LabeledField>
                        <LabeledField
                            label="Timezone"
                            helperText="All dates and times below are interpreted in this timezone."
                        >
                            <TimezoneSelect
                                value={values.timezone}
                                onChange={(value) =>
                                    setFieldValue('timezone', value)
                                }
                                style={{ width: 320 }}
                            />
                        </LabeledField>
                        <LabeledField label="Start date">
                            <DatePicker
                                allowClear={false}
                                format={DATE_TIME_FORMAT}
                                showTime={{ format: 'HH:mm', minuteStep: 15 }}
                                value={values.startDate}
                                onChange={(date) =>
                                    setFieldValue('startDate', date)
                                }
                            />
                        </LabeledField>
                        <LabeledField label="End date">
                            <DatePicker
                                allowClear={false}
                                format={DATE_TIME_FORMAT}
                                showTime={{ format: 'HH:mm', minuteStep: 15 }}
                                value={values.endDate}
                                onChange={(date) =>
                                    setFieldValue('endDate', date)
                                }
                            />
                        </LabeledField>
                        <LabeledField label="Active">
                            <Switch
                                className={style.toggle}
                                checked={values.isActive}
                                onChange={(checked) =>
                                    setFieldValue('isActive', checked)
                                }
                            />
                        </LabeledField>
                        {values.type === 'coupon' && (
                            <LabeledField
                                label="Private"
                                helperText="Private prize coupons are granted as winnings and never appear in the public promotions list."
                            >
                                <Switch
                                    className={style.toggle}
                                    checked={values.isPrivate}
                                    onChange={(checked) =>
                                        setFieldValue('isPrivate', checked)
                                    }
                                />
                            </LabeledField>
                        )}
                    </div>
                </Section>

                <Section title="Config">
                    <div className={style.config_fields}>
                        <LabeledField label="Category">
                            <Select
                                loading={
                                    categoryStore.isFetchingActiveCategories
                                }
                                value={values.categoryId}
                                onChange={(id) =>
                                    setFieldValue('categoryId', id)
                                }
                                placeholder="Select a category"
                            >
                                {categoryStore.activeCategories?.results.map(
                                    (category) => (
                                        <Select.Option
                                            value={category.id}
                                            key={category.id}
                                        >
                                            {category.items[0]?.title}
                                        </Select.Option>
                                    )
                                )}
                            </Select>
                        </LabeledField>
                        <LabeledField label="Promotional offer (optional)">
                            <Select
                                allowClear
                                disabled={
                                    !offerStore.offers ||
                                    offerStore.isFetchingOffers
                                }
                                loading={offerStore.isFetchingOffers}
                                value={values.promotionalOfferId ?? undefined}
                                onChange={(id) =>
                                    setFieldValue(
                                        'promotionalOfferId',
                                        id ?? null
                                    )
                                }
                                placeholder="Select an offer"
                            >
                                {offerStore.offers?.results.map((offer) => (
                                    <Select.Option
                                        value={offer.id}
                                        key={offer.id}
                                    >
                                        {offer.items[0]?.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </LabeledField>
                        <LabeledField label="Region">
                            <BusinessUnitsSelectTree
                                value={values.provinceIds}
                                onChange={(ids) =>
                                    setFieldValue(
                                        'provinceIds',
                                        cleanProvinceIds(ids)
                                    )
                                }
                            />
                        </LabeledField>
                        {values.type === 'coupon' && (
                            <LabeledField label="Activation code (barcode, 12 digits)">
                                <Input
                                    value={values.activationCode}
                                    inputMode="numeric"
                                    onChange={(e) => {
                                        const digits = e.target.value
                                            .replace(/\D/g, '')
                                            .slice(0, 12);
                                        setFieldValue('activationCode', digits);
                                    }}
                                    suffix={
                                        <span>
                                            {values.activationCode.length}/12
                                        </span>
                                    }
                                />
                            </LabeledField>
                        )}
                        {values.type === 'coupon' && (
                            <LabeledField label="Quantity">
                                <InputNumber
                                    min={1}
                                    value={values.quantity}
                                    onChange={(value) =>
                                        setFieldValue(
                                            'quantity',
                                            typeof value === 'number'
                                                ? value
                                                : 0
                                        )
                                    }
                                />
                            </LabeledField>
                        )}
                        <LabeledField label="Discount value (optional)">
                            <InputNumber
                                min={0}
                                value={values.discountValue ?? undefined}
                                onChange={(value) =>
                                    setFieldValue(
                                        'discountValue',
                                        typeof value === 'number' ? value : null
                                    )
                                }
                                placeholder="Optional"
                            />
                        </LabeledField>
                        <LabeledField label="Rebate amount">
                            <InputNumber
                                min={0}
                                value={values.rebateAmount}
                                onChange={(value) =>
                                    setFieldValue(
                                        'rebateAmount',
                                        typeof value === 'number' ? value : 0
                                    )
                                }
                            />
                        </LabeledField>
                        <LabeledField label="Original price">
                            <InputNumber
                                min={0}
                                value={values.originalPrice}
                                onChange={(value) =>
                                    setFieldValue(
                                        'originalPrice',
                                        typeof value === 'number' ? value : 0
                                    )
                                }
                            />
                        </LabeledField>
                        {isEdit && inventory && values.type === 'coupon' && (
                            <LabeledField
                                label="Inventory"
                                helperText="Granted and remaining are read-only."
                            >
                                <span>
                                    Granted: {inventory.granted} · Remaining:{' '}
                                    {inventory.remaining ?? '—'}
                                </span>
                            </LabeledField>
                        )}
                    </div>
                </Section>

                <Section title="Content">
                    <div className="columns_layout">
                        {values.items.map((item, i) => {
                            const localizedLanguage = localizeLanguage(
                                item.language
                            );
                            const localizedLanguageShort = localizeLanguageShort(
                                item.language
                            );
                            const isEnglish = item.language === 'en';
                            return (
                                <div key={item.language} className="column">
                                    <span
                                        className="h5_bold"
                                        style={{ marginBottom: 0 }}
                                    >
                                        {localizedLanguage}
                                        {isEnglish ? ' *' : ' (optional)'}
                                    </span>

                                    <div className={style.image_uploaders}>
                                        <LabeledField
                                            label={`Thumbnail (${localizedLanguageShort})`}
                                        >
                                            <ImageInput
                                                url={item.thumbnail}
                                                onChange={(url) =>
                                                    setFieldValue(
                                                        `items[${i}].thumbnail`,
                                                        url
                                                    )
                                                }
                                                onDelete={() =>
                                                    setFieldValue(
                                                        `items[${i}].thumbnail`,
                                                        null
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                        <LabeledField
                                            label={`Background (${localizedLanguageShort})`}
                                        >
                                            <ImageInput
                                                url={item.background}
                                                onChange={(url) =>
                                                    setFieldValue(
                                                        `items[${i}].background`,
                                                        url
                                                    )
                                                }
                                                onDelete={() =>
                                                    setFieldValue(
                                                        `items[${i}].background`,
                                                        null
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                    </div>

                                    <LabeledField
                                        label={`Title (${localizedLanguage})`}
                                    >
                                        <Input
                                            name={`items[${i}].title`}
                                            value={item.title}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </LabeledField>

                                    <LabeledField
                                        label={`Subtitle (${localizedLanguage})`}
                                    >
                                        <Input
                                            name={`items[${i}].subTitle`}
                                            value={item.subTitle}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </LabeledField>

                                    <LabeledField
                                        label={`Description (${localizedLanguage})`}
                                    >
                                        <RichText
                                            text={item.description}
                                            onChange={(text) =>
                                                setFieldValue(
                                                    `items[${i}].description`,
                                                    text
                                                )
                                            }
                                        />
                                    </LabeledField>
                                </div>
                            );
                        })}
                    </div>
                </Section>
            </div>
        </>
    );
});
